// DID IdentityKey admin client — built directly against the real, already-complete backend:
// BaseIdentityKeyController ([Route("api/v1/did/identity-keys")], confirmed by reading the
// controller directly). Follows TeamAdminApiClient's exact conventions (post = Data-wrapped
// Create/Update, postQuery = root-level-spread Search/Query, del = root-level DeleteWebRequest).
//
// Placed here (not a dedicated DID package) per the explicit directive to colocate with
// AgentPicker/CredentialPicker — this is the package Flow Studio's node config panel already
// pulls its inline lookup/manager components from. Domain-wise this is DID, not AI-agent, but
// following the established host-package convention was prioritized over a new package for this
// pass — worth revisiting if this package accumulates more non-agent domains.

import { BaseAdminApiClient, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';

// Must match BizFirst.Node.Crypto.Abstractions.Models.NodeCryptoAlgorithm's string constants
// exactly (Ed25519 / secp256k1-ethereum / secp256k1-bitcoin) — these round-trip as-is into
// Did_IdentityKeys.Algorithm with no translation table, so any drift here silently breaks the
// signing engine's per-algorithm strategy dispatch for keys created through this UI.
export type IdentityAlgorithm = 'Ed25519' | 'secp256k1-ethereum' | 'secp256k1-bitcoin';
export type IdentityKeyUsage = 'Signing' | 'KeyAgreement' | 'Both';

export interface IdentityKeyDTO {
  IdentityKeyID: number;
  CredentialID: number;
  KeyId: string;
  Algorithm: string;
  KeyUsage: string;
  PublicKeyJwk?: string | null;
  VaultProvider: string;
  VaultKeyName?: string | null;
  IsRevoked: boolean;
  RevokedOn?: string | null;
  RevokedReason?: string | null;
  NodeID?: number | null;
  IsPrimary?: boolean;
  TenantID: number;
}

export interface CreateIdentityKeyInput {
  CredentialID: number;
  KeyId: string;
  Algorithm: IdentityAlgorithm;
  KeyUsage: IdentityKeyUsage;
  VaultProvider: string;
  NodeID?: number;
  IsPrimary?: boolean;
  PublicKeyJwk?: string;
}

export class IdentityKeyAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  /** All keys for a given node — GetByNodeIdAsync's own route (by-credential is credential-scoped, not node-scoped; kept separate). */
  async getByNodeId(nodeId: number): Promise<AdminApiResult<IdentityKeyDTO[]>> {
    const result = await this.postQuery<IdentityKeyDTO[]>('/api/v1/did/identity-keys/by-node', {
      NodeID: { ID: nodeId },
      PageInfo: { PageNumber: 1, PageSize: 200 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async create(input: CreateIdentityKeyInput): Promise<AdminApiResult<{ id?: { id?: number } }>> {
    return this.post<{ id?: { id?: number } }>('/api/v1/did/identity-keys', input);
  }

  async update(identityKeyId: number, input: Partial<CreateIdentityKeyInput>): Promise<AdminApiResult<void>> {
    return this.put<void>(`/api/v1/did/identity-keys/${identityKeyId}`, { IdentityKeyID: identityKeyId, ...input });
  }

  /** Revoke, not delete — mirrors Did_IdentityKeys.RevokedReason; a key is never silently removed. */
  async revoke(identityKeyId: number, reason: string): Promise<AdminApiResult<void>> {
    return this.postQuery<void>(`/api/v1/did/identity-keys/${identityKeyId}/revoke`, {
      IdentityKeyID: { ID: identityKeyId },
      RevokedReason: reason,
    });
  }
}
